import { useCallback, useEffect, useRef, useState } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { flushSync } from 'react-dom'

import config from '@config'

const {
  abTest: { windowKey },
} = config

export type FeatureFlagsEntries = (typeof config.abTest)['featureFlagsEntries']

const getABTestValue = <T extends keyof FeatureFlagsEntries>(
  abTestKey: T,
  queryClient: QueryClient,
) =>
  queryClient.getQueryData<FeatureFlagsEntries[T]['values'][number]['name']>([
    windowKey,
    abTestKey,
  ])

function useABTestValue<T extends keyof FeatureFlagsEntries>(
  abTestKey: T,
): FeatureFlagsEntries[T]['values'][number]['name'] | undefined {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()

  const [abTestValue, setABTestValue] = useState<
    FeatureFlagsEntries[T]['values'][number]['name'] | undefined
  >(() => getABTestValue(abTestKey, queryClient))

  const unsubscribeFnRef = useRef<() => void>(undefined)

  const updateABTestValue = useCallback(
    (args: any) => {
      if (
        args.query.queryKey[0] === windowKey &&
        args.query.queryKey[1] === abTestKey
      ) {
        if (args?.action?.type === 'invalidate') {
          //! flushSync is used to ensure that the state is updated immediately,
          //! because that way we can reset the abtest value and set it again right after.
          //! Do **not** use this in other cases.
          flushSync(() => {
            setABTestValue(getABTestValue(abTestKey, queryClient))
          })
        } else if (args?.type === 'removed') {
          //! flushSync is used to ensure that the state is updated immediately,
          //! because that way we can reset the abtest value and set it again right after.
          //! Do **not** use this in other cases.
          flushSync(() => {
            setABTestValue(undefined)
          })
        }
      }
    },
    [abTestKey, queryClient],
  )

  useEffect(() => {
    setABTestValue(getABTestValue(abTestKey, queryClient))

    unsubscribeFnRef.current = queryCache.subscribe(updateABTestValue)

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [updateABTestValue, queryCache, queryClient, abTestKey])

  return abTestValue
}

export { getABTestValue }

export default useABTestValue
